import { useCallback, useSyncExternalStore } from 'react'
import { toScheduleEventId } from './diffScheduleEvents'
import {
  type FingerprintableEvent,
  fingerprintScheduleEvent,
} from './fingerprintScheduleEvent'

type Listener = () => void

const events = new Map<number, unknown>()
const listeners = new Map<number, Set<Listener>>()

const notify = (id: number): void => {
  const set = listeners.get(id)
  if (!set) return
  for (const listener of set) listener()
}

const toStoreId = (id: number | string): number => toScheduleEventId(id)

const hasVisibleScheduleChange = (previous: unknown, next: unknown): boolean => {
  if (previous === next) return false
  if (previous == null || next == null) return true
  if (typeof previous !== 'object' || typeof next !== 'object') return true

  return (
    fingerprintScheduleEvent(previous as FingerprintableEvent) !==
    fingerprintScheduleEvent(next as FingerprintableEvent)
  )
}

export const scheduleEventStore = {
  get: (id: number | string): unknown => events.get(toStoreId(id)),
  set: (id: number | string, data: unknown): void => {
    const storeId = toStoreId(id)
    const previous = events.get(storeId)
    events.set(storeId, data)
    if (hasVisibleScheduleChange(previous, data)) notify(storeId)
  },
  remove: (id: number | string): void => {
    const storeId = toStoreId(id)
    if (events.delete(storeId)) notify(storeId)
  },
  reset: (): void => {
    const previousIds = [...events.keys()]
    events.clear()
    for (const id of previousIds) notify(id)
  },
  hydrate: <T extends { id: number | string }>(items: T[]): void => {
    const previousById = new Map(events)
    const nextIds = new Set<number>()
    events.clear()
    const changedIds: number[] = []

    for (const item of items) {
      const storeId = toStoreId(item.id)
      nextIds.add(storeId)
      const previous = previousById.get(storeId)
      if (previous && !hasVisibleScheduleChange(previous, item)) {
        events.set(storeId, previous)
        continue
      }
      events.set(storeId, item)
      changedIds.push(storeId)
    }

    for (const id of previousById.keys()) {
      if (!nextIds.has(id)) {
        changedIds.push(id)
      }
    }

    for (const id of new Set(changedIds)) notify(id)
  },
  subscribe: (id: number | string, listener: Listener): (() => void) => {
    const storeId = toStoreId(id)
    let set = listeners.get(storeId)
    if (!set) {
      set = new Set()
      listeners.set(storeId, set)
    }
    set.add(listener)
    return () => {
      set.delete(listener)
      if (set.size === 0) listeners.delete(storeId)
    }
  },
}

export function useScheduleEvent<T>(id: number | string | undefined, fallback: T): T {
  const eventId = id == null || id === '' ? undefined : toStoreId(id)
  const subscribe = useCallback(
    (listener: Listener) =>
      eventId == null || Number.isNaN(eventId)
        ? () => undefined
        : scheduleEventStore.subscribe(eventId, listener),
    [eventId],
  )
  const getSnapshot = useCallback(
    () => (eventId == null || Number.isNaN(eventId) ? undefined : scheduleEventStore.get(eventId)),
    [eventId],
  )
  const stored = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return (stored as T) ?? fallback
}
